import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Chip,
  Avatar,
  Card,
  CardContent,
  IconButton,
  Divider,
  FormControl,
  Select,
  MenuItem,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputLabel,
  ButtonGroup,
  Tabs,
  Tab,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Menu,
  CircularProgress,
  Alert,
  LinearProgress,
  Snackbar,
  Slide
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  LocationOn as LocationIcon,
  Visibility as ViewIcon,
  Description as InvoiceIcon,
  FilterList as FilterIcon,
  VerifiedUser as VerifiedIcon,
  TrendingUp as TrendingUpIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  TableChart as TableChartIcon,
  ViewModule as ViewModuleIcon,
  FileDownload as ExportIcon,
  DateRange as DateRangeIcon,
  ArrowUpward as ArrowUpwardIcon,
  GetApp as DownloadIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { supplierService } from '../utils/apiService';
import ExportButton from '../components/ExportButton';
import ConfirmationModal from '../components/print/ConfirmationModal';
import * as XLSX from 'xlsx';

function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [viewMode, setViewMode] = useState('table');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [filters, setFilters] = useState({
    status: {
      active: true,
      inactive: true,
    }
  });

  // New import/export state
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importSuccess, setImportSuccess] = useState(null);
  const [importFailedRecords, setImportFailedRecords] = useState([]); // New state for failed records
  const [parsedRows, setParsedRows] = useState([]);   // SheetJS parsed data
  const [parsedHeaders, setParsedHeaders] = useState([]);  // column headers

  // Field mapping state
  const [mappingModalOpen, setMappingModalOpen] = useState(false);
  const [bulkFields, setBulkFields] = useState([]);      // API fields
  const [bulkFieldsLoading, setBulkFieldsLoading] = useState(false);
  const [bulkFieldsError, setBulkFieldsError] = useState(null);
  const [fieldMapping, setFieldMapping] = useState({});  // { apiField: excelHeader | '' }

  // Toast / Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });
  const handleSnackbarClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((s) => ({ ...s, open: false }));
  };

  const exportColumns = [
    { field: 'name', headerName: 'Name', width: 200 },
    { field: 'vatRegistrationNumber', headerName: 'VAT Number', width: 150 },
    { field: 'email', headerName: 'Email', width: 200 },
    { field: 'address.street', headerName: 'Street', width: 200 },
    { field: 'address.city', headerName: 'City', width: 150 },
    { field: 'address.state', headerName: 'State', width: 150 },
    { field: 'address.postalCode', headerName: 'Postal Code', width: 120 },
    { field: 'address.country', headerName: 'Country', width: 150 },
    { field: 'isActive', headerName: 'Status', width: 100 }
  ];

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await supplierService.getSuppliers(page + 1, rowsPerPage);
      console.log('Suppliers API response:', response); // Debug log

      if (Array.isArray(response)) {
        setSuppliers(response);
        setTotalCount(response.length);
      } else if (response && Array.isArray(response.suppliers)) {
        setSuppliers(response.suppliers);
        setTotalCount(response.total || response.suppliers.length);
      } else {
        console.error('Invalid response format:', response);
        setError('Invalid response format from server');
        setSuppliers([]);
      }
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      setError(err.message || 'Failed to load suppliers');
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleImportSample = async () => {
    try {
      setLoading(true);
      const response = await supplierService.downloadBulkSample();

      const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'suppliers_sample.xlsx');
      document.body.appendChild(link);
      link.click();

      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      setError(null);
    } catch (err) {
      console.error('Error downloading sample:', err);
      setError(err.message || 'Failed to download sample file');
    } finally {
      setLoading(false);
    }
  };

  const buildDefaultMapping = (apiFields, excelHeaders) => {
    const mapping = {};
    apiFields.forEach((field) => {
      const fieldName = typeof field === 'object' ? (field.key || field.name || field.field || '') : field;
      const match = excelHeaders.find(
        (h) => h.toLowerCase().replace(/[\s_-]/g, '') === fieldName.toLowerCase().replace(/[\s_-]/g, '')
      );
      mapping[fieldName] = match || '';
    });
    return mapping;
  };

  const openMappingModal = async (file, headers, rows) => {
    setMappingModalOpen(true);
    setBulkFieldsLoading(true);
    setBulkFieldsError(null);
    try {
      const data = await supplierService.getBulkFields();
      let fields = [];
      if (Array.isArray(data)) {
        fields = data;
      } else if (data && Array.isArray(data.fields)) {
        fields = data.fields;
      } else if (data && typeof data === 'object') {
        fields = Object.keys(data);
      }
      setBulkFields(fields);
      setFieldMapping(buildDefaultMapping(fields, headers));
    } catch (err) {
      console.error('Error fetching bulk fields:', err);
      setBulkFieldsError('Could not load API fields. You can still proceed with the raw data.');
      setBulkFields(headers);
      const fallback = {};
      headers.forEach((h) => { fallback[h] = h; });
      setFieldMapping(fallback);
    } finally {
      setBulkFieldsLoading(false);
    }
  };

  const handleImportExcel = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImportFile(file);
      setImportError(null);
      setImportSuccess(null);
      setImportFailedRecords([]);
      setParsedRows([]);
      setParsedHeaders([]);

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const rawRows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
          if (rawRows.length === 0) {
            setImportError('The file is empty or has no data rows.');
            setMappingModalOpen(true);
            return;
          }
          const headers = Object.keys(rawRows[0]);
          setParsedHeaders(headers);
          setParsedRows(rawRows);
          openMappingModal(file, headers, rawRows);
        } catch (err) {
          console.error('SheetJS parse error:', err);
          setImportError('Could not parse the file. Make sure it is a valid Excel or CSV file.');
          setMappingModalOpen(true);
        }
      };
      reader.onerror = () => {
        setImportError('Failed to read the file.');
        setMappingModalOpen(true);
      };
      reader.readAsArrayBuffer(file);
    }
    event.target.value = '';
  };

  const handleCloseMappingModal = () => {
    if (!importLoading) {
      setMappingModalOpen(false);
    }
  };

  const handleMappingChange = (apiField, excelHeader) => {
    setFieldMapping((prev) => ({ ...prev, [apiField]: excelHeader }));
  };

  const handleConfirmImport = async () => {
    if (!importFile || !parsedRows.length) return;
    setImportLoading(true);
    setImportError(null);
    setImportSuccess(null);

    try {
      console.log('--- BULK UPLOAD INVESTIGATION ---');
      console.log('1. Parsed Excel Headers:', parsedHeaders);
      console.log('2. First Parsed Row (Before Mapping):', parsedRows[0]);
      console.log('3. Current Field Mapping:', fieldMapping);

      // Create transformed mapping and rows
      const cleanedMapping = {};
      const validationErrors = [];
      const transformedRows = [];

      for (let i = 0; i < parsedRows.length; i++) {
        const row = parsedRows[i];
        const newRow = {};

        Object.entries(fieldMapping).forEach(([apiField, excelHeader]) => {
          if (excelHeader && excelHeader.trim() !== '') {
            cleanedMapping[apiField] = apiField;

            let val = row[excelHeader];
            // Handle and clean values
            if (val !== undefined && val !== null) {
              if (typeof val === 'string') {
                val = val.trim();
              }
              // Only assign if it's not empty after trimming
              if (val !== '') {
                newRow[apiField] = val;
              }
            }
          }
        });

        // Validation for mandatory fields
        const rowNum = i + 2; // Excel row number (assuming header is row 1)
        if (!newRow.name) {
          validationErrors.push(`Row ${rowNum}: "name" is required but is missing or empty.`);
        }

        transformedRows.push(newRow);
      }

      if (Object.keys(cleanedMapping).length === 0) {
        throw new Error('Please map at least one field before importing.');
      }

      if (validationErrors.length > 0) {
        // Show the first few errors to the user clearly
        const errorMsg = `Validation failed for ${validationErrors.length} row(s):\n` +
          validationErrors.slice(0, 3).join('\n') +
          (validationErrors.length > 3 ? `\n...and ${validationErrors.length - 3} more errors.` : '');
        throw new Error(errorMsg);
      }

      // Pass cleanedMapping directly since supplierService.bulkImport wraps it in fieldMap
      const exactHeaders = Object.keys(cleanedMapping);
      const worksheet = XLSX.utils.json_to_sheet(transformedRows, { header: exactHeaders });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Suppliers');
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

      const newFile = new File([excelBuffer], importFile.name, {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const result = await supplierService.bulkImport(newFile, cleanedMapping);

      const importedCount =
        result?.imported ??
        result?.insertedCount ??
        result?.count ??
        transformedRows.length;
      const failedCount = result?.failed ?? result?.failedCount ?? 0;
      const failedRecords = result?.failedRecords || result?.errors || [];

      const successMsg =
        failedCount > 0
          ? `Imported ${importedCount} supplier(s) — ${failedCount} skipped/failed. Check your data.`
          : `Successfully Created.`;

      if (failedRecords.length > 0 || failedCount > 0) {
        setImportError(`Import completed with ${failedCount} failed records.`);
        if (failedRecords.length > 0) {
          setImportFailedRecords(failedRecords);
        }
        showSnackbar(successMsg, 'warning');
        // Refresh to show any successfully imported records
        fetchSuppliers();
      } else {
        setMappingModalOpen(false);
        setParsedRows([]);
        setParsedHeaders([]);
        setFieldMapping({});
        setImportFile(null);
        setImportError(null);
        setImportSuccess(null);
        setImportFailedRecords([]);
        showSnackbar(successMsg, 'success');
        fetchSuppliers();
      }
    } catch (err) {
      console.error('Error importing excel:', err);
      setImportError(
        err?.message || 'Import failed. Please check the file and field mapping, then try again.'
      );
      if (err.failedRecords) {
        setImportFailedRecords(err.failedRecords);
      }
    } finally {
      setImportLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
  }, [page, rowsPerPage]);

  const handleDeleteClick = (supplier) => {
    setSelectedSupplier(supplier);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      if (selectedSupplier) {
        await supplierService.deleteSupplier(selectedSupplier._id);
        fetchSuppliers();
      }
    } catch (error) {
      console.error('Error deleting supplier:', error);
    } finally {
      setDeleteModalOpen(false);
      setSelectedSupplier(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSelectedSupplier(null);
  };

  const handleEditClick = (supplier) => {
    if (supplier && supplier._id) {
      navigate(`/suppliers/edit/${supplier._id}`);
    } else {
      console.error('Cannot edit supplier: Invalid supplier ID', supplier);
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleFilterChange = (category, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [category]: {
        ...prevFilters[category],
        [value]: !prevFilters[category][value]
      }
    }));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredSuppliers = suppliers.filter(supplier => {
    // Search filter
    const searchLower = searchQuery ? searchQuery.toLowerCase() : '';
    const matchesSearch = !searchQuery || searchQuery === '' ||
      ((supplier.name && supplier.name.toLowerCase().includes(searchLower)) ||
        (supplier.email && supplier.email.toLowerCase().includes(searchLower)) ||
        (supplier.vatRegistrationNumber && supplier.vatRegistrationNumber.toLowerCase().includes(searchLower)) ||
        (supplier.code && supplier.code.toLowerCase().includes(searchLower)) ||
        (supplier.phone && supplier.phone.toLowerCase().includes(searchLower)) ||
        (supplier.address?.street && supplier.address.street.toLowerCase().includes(searchLower)));

    // Status filter
    const matchesStatus =
      (filters.status.active && supplier.isActive) ||
      (filters.status.inactive && !supplier.isActive);

    return matchesSearch && matchesStatus;
  });

  return (
    <Container maxWidth={false} sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Box>
          <Typography variant="h5" component="h1" fontWeight="bold">
            Suppliers
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            View all your suppliers information
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button
            variant="outlined"

            size="small"
            onClick={handleImportSample}
            sx={{
              borderRadius: '4px',
              textTransform: 'none',
              py: 0.5,
              px: 1,
            }}
          >
            <DownloadIcon />

          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            size="small"
            component="label"
            sx={{
              borderRadius: '4px',
              textTransform: 'none',
              py: 0.5,
              px: 1,
            }}
          >
            Import
            <input
              type="file"
              hidden
              accept=".xlsx,.xls,.csv"
              onChange={handleImportExcel}
            />
          </Button>

          <ExportButton
            data={filteredSuppliers}
            columns={exportColumns}
            filename="suppliers_export"
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            size="small"
            onClick={() => navigate('/suppliers/new')}
            sx={{
              borderRadius: '4px',
              textTransform: 'none',
              py: 0.5,
              px: 1,
              background: 'linear-gradient(45deg, #4361ee 0%, #4895ef 100%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #3b54d6 0%, #3d7dce 100%)',
              },
            }}
          >
            Add Supplier
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search for suppliers..."
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              width: '100%',
              maxWidth: 500,
              height: 28,
              '& .MuiOutlinedInput-root': {
                borderRadius: '2px',
                height: 28,
              },
              '& .MuiInputBase-input': {
                py: 0.5,
              }
            }}
          />
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            size="small"
            onClick={handleFilterClick}
            sx={{
              borderRadius: '2px',
              textTransform: 'none',
              py: 0.25,
              px: 1,
              height: 28,
              ml: 'auto',
            }}
          >
            Filters
          </Button>
        </Box>
      </Box>

      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterClose}
        PaperProps={{
          sx: {
            width: 200,
            p: 1,
          },
        }}
      >
        <FormGroup>
          <Typography variant="subtitle2" sx={{ fontWeight: 'medium', mb: 0.5 }}>Status</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={filters.status.active}
                onChange={() => handleFilterChange('status', 'active')}
                size="small"
              />
            }
            label="Active"
            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={filters.status.inactive}
                onChange={() => handleFilterChange('status', 'inactive')}
                size="small"
              />
            }
            label="Inactive"
            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
          />
        </FormGroup>
      </Menu>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          p: 0.5,
          mb: 0.5,
          border: '1px solid rgba(0, 0, 0, 0.12)',
          borderRadius: '4px',
        }}
      >
        <Box>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, width: '200px' }}>Code</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '200px' }}>Name</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '200px' }}>Email</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '200px' }}>Address</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '150px' }}>VAT No</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '120px' }}>Phone No</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '120px' }}>City</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '100px' }}>Status</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, width: '100px' }}>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : filteredSuppliers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                      No suppliers found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSuppliers.map((supplier) => (
                    <TableRow
                      key={supplier._id}
                      hover
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell align="left" sx={{ py: 0.5 }}>{supplier.code || '-'}</TableCell>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'medium', py: 0.5 }}>
                        {supplier.name || '-'}
                      </TableCell>
                      <TableCell align="left" sx={{ py: 0.5 }}>{supplier.email || '-'}</TableCell>
                      <TableCell align="left" sx={{ py: 0.5 }}>{supplier.address?.street || '-'}</TableCell>
                      <TableCell align="left" sx={{ py: 0.5 }}>{supplier.vatRegistrationNumber || '-'}</TableCell>
                      <TableCell align="left" sx={{ py: 0.5 }}>{supplier.phone || '-'}</TableCell>
                      <TableCell align="left" sx={{ py: 0.5 }}>{supplier.address?.city || '-'}</TableCell>
                      <TableCell align="center" sx={{ py: 0.5 }}>
                        <Chip
                          label={supplier.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={supplier.isActive ? 'success' : 'error'}
                          variant="outlined"
                          sx={{
                            fontWeight: 'medium',
                            backgroundColor: !supplier.isActive ? 'rgba(211, 47, 47, 0.1)' : 'transparent'
                          }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ py: 0.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <IconButton
                            size="small"
                            color="primary"
                            sx={{ mx: 0.25, p: 0.5 }}
                            onClick={() => handleEditClick(supplier)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            sx={{ mx: 0.25, p: 0.5 }}
                            onClick={() => handleDeleteClick(supplier)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: '1px solid rgba(0, 0, 0, 0.12)',
              '& .MuiTablePagination-select': {
                py: 0.5,
              },
              '& .MuiTablePagination-displayedRows': {
                fontSize: '0.875rem',
              }
            }}
          />
        </Box>
      </Paper>

      <ConfirmationModal
        open={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Supplier"
        message={`Are you sure you want to delete "${selectedSupplier?.name}"?`}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* ── Field Mapping Modal ─────────────────────────────────────────── */}
      <Dialog
        open={mappingModalOpen}
        onClose={handleCloseMappingModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '10px',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            fontWeight: 700,
            fontSize: '1rem',
            background: 'linear-gradient(45deg, #4361ee 0%, #4895ef 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          Map Excel Columns to Supplier Fields
          {importFile && (
            <Typography
              variant="caption"
              sx={{ ml: 'auto', opacity: 0.85, fontWeight: 400 }}
            >
              {importFile.name} &nbsp;·&nbsp; {parsedRows.length} rows
            </Typography>
          )}
        </DialogTitle>

        <DialogContent sx={{ pt: 2.5, mt: '20px' }}>
          {importLoading && <LinearProgress sx={{ mb: 2 }} />}

          {importError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {importError}
            </Alert>
          )}

          {importFailedRecords && importFailedRecords.length > 0 && (
            <Box sx={{ mb: 3, mt: 1 }}>
              <Typography variant="subtitle2" color="error" gutterBottom>
                Failed Records Details
              </Typography>
              <Box sx={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #ffcdd2', borderRadius: '8px', bgcolor: '#fff' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: '#ffebee', fontWeight: 600, width: '100px' }}>Row / Status</TableCell>
                      <TableCell sx={{ bgcolor: '#ffebee', fontWeight: 600 }}>Record Data</TableCell>
                      <TableCell sx={{ bgcolor: '#ffebee', fontWeight: 600 }}>Error Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importFailedRecords.map((record, index) => {
                      const rowNum = record.rowNumber || record.row || (index + 1);
                      const errors = record.errors || record.error;

                      const renderErrors = (errs) => {
                        if (!errs) return <Typography variant="caption" color="error">Unknown error</Typography>;
                        if (typeof errs === 'string') return <Typography variant="caption" color="error">{errs}</Typography>;
                        if (Array.isArray(errs)) {
                          return (
                            <ul style={{ margin: 0, paddingLeft: '20px', color: '#d32f2f' }}>
                              {errs.map((err, i) => (
                                <li key={i}>
                                  <Typography variant="caption">
                                    {typeof err === 'object' ? (
                                      <><strong>{err.field || 'Error'}:</strong> {err.message || err.error || JSON.stringify(err)}</>
                                    ) : (
                                      err
                                    )}
                                  </Typography>
                                </li>
                              ))}
                            </ul>
                          );
                        }
                        return (
                          <ul style={{ margin: 0, paddingLeft: '20px', color: '#d32f2f' }}>
                            {Object.entries(errs).map(([field, msg]) => (
                              <li key={field}>
                                <Typography variant="caption">
                                  <strong>{field}:</strong> {typeof msg === 'string' ? msg : JSON.stringify(msg)}
                                </Typography>
                              </li>
                            ))}
                          </ul>
                        );
                      };

                      return (
                        <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Typography variant="body2" fontWeight="bold">
                              Row {rowNum}
                            </Typography>
                            <Chip label="Failed" size="small" color="error" sx={{ mt: 1, height: 20, fontSize: '0.7rem' }} />
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            {record.data || record.record ? (
                              <Box>
                                {(record.data?.name || record.record?.name) && <Typography variant="caption" display="block"><strong>Name:</strong> {record.data?.name || record.record?.name}</Typography>}
                                {(record.data?.code || record.record?.code) && <Typography variant="caption" display="block"><strong>Code:</strong> {record.data?.code || record.record?.code}</Typography>}
                                {(record.data?.email || record.record?.email) && <Typography variant="caption" display="block"><strong>Email:</strong> {record.data?.email || record.record?.email}</Typography>}
                                {(record.data?.vatRegistrationNumber || record.record?.vatRegistrationNumber) && <Typography variant="caption" display="block"><strong>VAT:</strong> {record.data?.vatRegistrationNumber || record.record?.vatRegistrationNumber}</Typography>}
                              </Box>
                            ) : '-'}
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            {renderErrors(errors)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )}

          {bulkFieldsError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {bulkFieldsError}
            </Alert>
          )}

          {bulkFieldsLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4, justifyContent: 'center' }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                Loading API fields…
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1.5,
                maxHeight: 380,
                overflowY: 'auto',
                pr: 0.5,
              }}
            >
              {bulkFields.map((field) => {
                const apiField =
                  typeof field === 'object'
                    ? field.key || field.name || field.field || JSON.stringify(field)
                    : field;
                const label =
                  typeof field === 'object'
                    ? field.label || field.displayName || apiField
                    : field;
                const required =
                  typeof field === 'object' ? !!field.required : false;

                return (
                  <Box
                    key={apiField}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1,
                      borderRadius: '6px',
                      border: '1px solid rgba(0,0,0,0.08)',
                      bgcolor: 'rgba(67,97,238,0.02)',
                    }}
                  >
                    <Box sx={{ flex: '0 0 auto', minWidth: 140 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: required ? 'error.main' : 'text.primary',
                          fontSize: '0.78rem',
                        }}
                      >
                        {label}
                        {required && (
                          <Typography component="span" sx={{ color: 'error.main', ml: 0.25 }}>
                            *
                          </Typography>
                        )}
                      </Typography>
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{ color: 'text.disabled', flex: '0 0 auto' }}
                    >
                      ←
                    </Typography>

                    <FormControl size="small" sx={{ flex: 1 }}>
                      <Select
                        value={fieldMapping[apiField] || ''}
                        onChange={(e) => handleMappingChange(apiField, e.target.value)}
                        displayEmpty
                        sx={{
                          fontSize: '0.8rem',
                          '& .MuiOutlinedInput-notchedOutline': { borderRadius: '4px' },
                        }}
                      >
                        <MenuItem value="">
                          <em style={{ color: '#999', fontSize: '0.8rem' }}>— not mapped —</em>
                        </MenuItem>
                        {parsedHeaders.map((h) => (
                          <MenuItem key={h} value={h} sx={{ fontSize: '0.82rem' }}>
                            {h}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 2.5,
            pt: 1,
            borderTop: '1px solid rgba(0,0,0,0.08)',
            gap: 1,
          }}
        >
          <Button
            onClick={handleCloseMappingModal}
            disabled={importLoading}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmImport}
            disabled={parsedRows.length === 0 || importLoading || bulkFieldsLoading}
            sx={{
              textTransform: 'none',
              borderRadius: '6px',
              background: 'linear-gradient(45deg, #4361ee 0%, #4895ef 100%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #3b54d6 0%, #3d7dce 100%)',
              },
            }}
          >
            {importLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                Creating...
              </Box>
            ) : (
              'Create'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Success / Warning Toast ─────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Slide}
        TransitionProps={{ direction: 'up' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: '8px', boxShadow: 4 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default Suppliers; 